'use client'

import { useQueryResults } from "@/hooks/useQueryResults";
import { useMemo, useState } from "react";
import { DatasetAreaChart } from "./charts/dataset-area-chart";
import { Percent, Sigma } from "lucide-react";

export function LockstakeSKYonSKYOverHoldingGroups() {
    return <TokenHoldingGroups id={6158493} tokenSymbol="SKY" title="Lockstake SKY/SKY Deposits"/>
}

export function SkyOverHoldingGroups() {
    return <TokenHoldingGroups id={6127312} tokenSymbol="SKY" />
}

export function SusdeOverHoldingGroups() {
    return <TokenHoldingGroups id={4511356} tokenSymbol="sUSDe" />
}

export function SyUSDCOverHoldingGroups() {
    return <TokenHoldingGroups id={5292252} tokenSymbol="syrupUSDC" />
}

export function SyUSDTOverHoldingGroups() {
    return <TokenHoldingGroups id={5292256} tokenSymbol="syrupUSDT" />
}

export function SusdsOverHoldingGroups() {
    return <TokenHoldingGroups id={4833785} tokenSymbol="sUSDS" dollarized/>
}

export function UsdsOverHoldingGroups() {
    return <TokenHoldingGroups id={4833782} tokenSymbol="USDS" dollarized/>
}

export function SusdcOverHoldingGroups() {
    return <TokenHoldingGroups id={5190070} tokenSymbol="sUSDC" />
}

export function DaiOverHoldingGroups() {
    return <TokenHoldingGroups id={5693648} tokenSymbol="DAI" dollarized/>
}

export function SdaiOverHoldingGroups() {
    return <TokenHoldingGroups id={6480552} tokenSymbol="sDAI" />
}

export function StusdsOverHoldingGroups() {
    return <TokenHoldingGroups id={5959722} tokenSymbol="stUSDS" dollarized/>
}

export function SpusdcOverHoldingGroups() {
    return <TokenHoldingGroups id={6015027} tokenSymbol="spUSDC" dollarized/>
}

export function SpusdtOverHoldingGroups() {
    return <TokenHoldingGroups id={6015033} tokenSymbol="spUSDT" dollarized/>
}

export function SpkOverHoldingGroups() {
    return <TokenHoldingGroups id={6127580} tokenSymbol="SPK"/>
}



function TokenHoldingGroups({id, tokenSymbol, dollarized, description, title}: {title?: string, id: number, tokenSymbol: string, dollarized?: boolean, description?: string}) {
    const { results } = useQueryResults({id})
    const [showPct, setShowPct] = useState(false)

    const formatter = new Intl.NumberFormat("en", {notation: "compact"})
    const tooltipFormatter = new Intl.NumberFormat("en", {maximumFractionDigits: 0})
    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', maximumFractionDigits: 2})

    const formattedData = useMemo(() => {
        if (!results) return {dimensions: [], source: []}
        const series = [...new Set(results.map(r => r.group as string)).keys()]
        const days = [...new Set(results.map(r => r.date.valueOf())).keys()].map((d) => new Date(d))
        const dimensions = ['date', ...series]
        const source = days.map(d => {
            const filtered = results.filter(r => r.date.valueOf() === d.valueOf())
            return [
                d, ...dimensions.slice(1).map(s => {
                    const data = filtered.filter(r => r.group === s).at(0)
                    if (!data) return 0
                    return (data.balance_usd || data.balance)
                }) as (string | number)[]
            ]
        }) 

        return {
            dimensions,
            source
        }
    }, [results])

    const pctData = useMemo(() => {
        return {
            dimensions: formattedData.dimensions,
            source: formattedData.source.map(s => {
                const sum = s.slice(1).reduce((a, i) => (a as number) + (i as number), 0) as number
                return [s[0], ...s.slice(1).map(v => (v as number)/sum)]
            })
        }
    }, [formattedData])

    const _switch = () => setShowPct(s => !s)

    return <div className="w-full h-[40vh]">
        <DatasetAreaChart
            dataset={showPct?pctData:formattedData}
            title={title || `${tokenSymbol} holding groups`}
            description={description}
            stack
            yAxisFormatter={(n) => showPct?percentFormatter.format(n):formatter.format(n)}
            tooltip={{
                seriesValueFormatter: (value) => showPct?percentFormatter.format(value):`${dollarized?'$':''}${tooltipFormatter.format(value)} ${dollarized?'':tokenSymbol}`
            }}
            showTotal={!showPct}
            legendActions={[
                {
                    name: `Switch to ${showPct?'nominal':'percent'} view`,
                    action: _switch,
                    icon: showPct?Sigma:Percent
                }
            ]}
        />
    </div>
}
